import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Loader } from 'semantic-ui-react';
import classnames from 'classnames';
import { onWallSaved, onWallShared } from 'arts/config/analytics';
import PanelLayout from '../panel/layout';
import * as shared from '../assets/shared.module.scss';
import * as classes from './classes.module.scss';
import Const from '../../config/const';
import WorkDetails from '../info/work_details';
import Preview from './preview';
import EditDescription from './edit_description';
import UploadLogo from './upload_logo';
import CopyText from './copy_text';
import Attributes from '../attributes';

export default class Export extends PureComponent {
  static propTypes = {
    closePanel: PropTypes.func.isRequired,
    savePreview: PropTypes.func.isRequired,
    publish: PropTypes.func.isRequired,
    showSharing: PropTypes.func.isRequired,
    updateAttributes: PropTypes.func.isRequired,
    setLogo: PropTypes.func.isRequired,
    resetLogo: PropTypes.func.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    worksExtended: PropTypes.array.isRequired,
    totalPrice: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    wallId: PropTypes.number.isRequired,
    // savingWall
    savingPictures: PropTypes.bool.isRequired,
    // exporting
    publishing: PropTypes.bool.isRequired,
    savingPreview: PropTypes.bool.isRequired,
    previewSaved: PropTypes.bool.isRequired,
    previewSavingError: PropTypes.object,
    previewSrc: PropTypes.string,
    logoSrc: PropTypes.string,
    // sharing
    sharingUrl: PropTypes.string
  };

  constructor(props) {
    super(props);
    this.state = {
      previewReady: false,
      previewReadyTransparent: false,
      showPrice: true,
      src: null,
      srcTransparent: null,
      editingDescription: false,
      uploadingLogo: false,
      editingAttributes: false
    };
  }

  componentDidUpdate({ publishing: prevPublishing }) {
    const { publishing } = this.props;
    if (publishing !== prevPublishing && !publishing) {
      // eslint-disable-next-line no-alert
      alert('Wall published');
    }
  }

  closePanel = () => {
    const { closePanel } = this.props;
    closePanel(Const.panelKinds.export);
  }

  savePreview = () => {
    const { savePreview, worksExtended } = this.props;
    const { src, srcTransparent, previewTimeStamp } = this.state;
    const itemsCount = (worksExtended || []).length;
    onWallSaved(itemsCount);
    savePreview(src, srcTransparent, previewTimeStamp);
  };

  onShareWall = () => {
    const { showSharing, worksExtended } = this.props;
    const itemsCount = (worksExtended || []).length;
    onWallShared(itemsCount);
    showSharing();
  };

  onBuildComplete = (src, previewReady) => {
    const newState = { previewReady, src };
    if (previewReady && src) {
      newState.previewTimeStamp = new Date().getTime();
    }
    this.setState(newState);
  };

  onBuildCompleteTransparent = (srcTransparent, previewReadyTransparent) => {
    const newState = { previewReadyTransparent, srcTransparent };
    this.setState(newState);
  };

  onBuildStarted = () => this.setState({ previewReady: false });

  onBuildStartedTransparent = () => this.setState({ previewReadyTransparent: false });

  onTogglePrice = () => this.setState((state) => ({ showPrice: !state.showPrice }));

  onEditDescription = () => this.setState({ editingDescription: true });

  onCancelEditingDescription = () => this.setState({ editingDescription: false });

  onUploadLogo = () => {
    const { logoSrc, resetLogo } = this.props;
    if (logoSrc) {
      resetLogo();
      return;
    }
    this.setState({ uploadingLogo: true });
  }

  onCancelUploadingLogo = () => this.setState({ uploadingLogo: false });

  exportPdf = () => {
    const { showPrice } = this.state;
    const { logoSrc } = this.props;
    document.querySelector('#pdf_form input[name=prices]').value = showPrice ? 'true' : 'false';
    document.querySelector('#pdf_form input[name=pdf_logo]').value = logoSrc || '';
    document.querySelector('#pdf_form').submit();
  };

  startEditingAttributes = () => this.setState({ editingAttributes: true });

  stopEditingAttributes = () => this.setState({ editingAttributes: false });

  renderWorks() {
    const { worksExtended, currency } = this.props;
    const { showPrice } = this.state;
    return worksExtended.map((w, index) => (
      <div key={ index }>
        <div className={ classes.work }>
          <div className={ classes.img }>
            <img src={ w.zoomPreviewUrl } alt={ w.title } />
          </div>
          <div className={ classes.workDetails }>
            <WorkDetails { ...w } columns showPrice={ showPrice } currency={ currency } />
          </div>
        </div>
        <div className={ classnames(shared.h4, shared.balanced) } />
      </div>
    ));
  }

  renderActions() {
    const {
      previewReady, showPrice, previewReadyTransparent, editingAttributes
    } = this.state;
    const {
      totalPrice, savingPreview, previewSavingError, previewSrc,
      publish, publishing, savingPictures, sharingUrl, worksExtended
    } = this.props;
    const disableControls = !totalPrice || !previewReadyTransparent
      || !previewReady || savingPreview || publishing || savingPictures;

    let renderStatus = null;
    if (previewSavingError) {
      renderStatus =  (
        <p className={ classes.saving }>
          Error saving preview, please try again.
        </p>
      );
    } else if (savingPreview) {
      renderStatus = (
        <div className={ classes.saving }>
          <Loader size="mini" active inline />
          <p>&nbsp;Saving preview, please wait...</p>
        </div>
      );
    } else if (publishing) {
      renderStatus = (
        <div className={ classes.saving }>
          <Loader size="mini" active inline />
          <p>&nbsp;Publishing, please wait...</p>
        </div>
      );
    }

    const renderPublish = window.canPublish ? (
      <Button
        className={ classes.action }
        disabled={ disableControls }
        onClick={ publish }
        color="black"
      >
        Publish
      </Button>
    ) : null;

    const renderEditAttributes = (window.canPublish && !editingAttributes) ? (
      <Button
        className={ classes.action }
        disabled={ disableControls }
        onClick={ this.startEditingAttributes }
        color="black"
      >
        Attributes...
      </Button>
    ) : null;

    if (publishing) {
      return (
        <>
          { renderPublish }
          { renderStatus }
        </>
      );
    }

    if (!previewSrc) {
      return (
        <>
          <Button
            className={ classes.action }
            disabled={ disableControls }
            color="black"
            onClick={ this.savePreview }
          >
            Save...
          </Button>
          { renderEditAttributes }
          { renderStatus }
        </>
      );
    }

    return (
      <>
        <div className={ classes.actionAlignment }>
          <Checkbox
            disabled={ disableControls }
            label="Show Price"
            checked={ showPrice }
            onChange={ this.onTogglePrice }
            className={ classes.showPrice }
          />
          <Button
            className={ classes.action }
            disabled={ disableControls }
            color="black"
            onClick={ this.exportPdf }
          >
            Export PDF
          </Button>
          { renderEditAttributes }
          { renderStatus }
        </div>
        <div className={ classes.actionAlignment }>
          <Button
            className={ classes.action }
            disabled={ disableControls }
            color="black"
            onClick={ this.onShareWall }
          >
            Share
          </Button>
          { renderPublish }
          <CopyText text={ sharingUrl } numItemsWall={ worksExtended.length } />
        </div>
      </>
    );
  }

  renderLogo() {
    const { logoSrc, setLogo } = this.props;
    const { uploadingLogo } = this.state;

    let content = null;
    if (uploadingLogo) {
      content = (
        <UploadLogo
          setLogo={ setLogo }
          onCancel={ this.onCancelUploadingLogo }
          src={ logoSrc }
        />
      );
    } else if (!logoSrc) {
      content = (
        <div className={ classes.addDescription }>
          UPLOAD YOUR LOGO
          <br />
          200x200px tops, PNG/JPG image
        </div>
      );
    } else if (logoSrc) {
      content = (
        <div className={ classes.logo }>
          <img src={ logoSrc } alt="Logo" />
        </div>
      );
    }

    const hoverTitle = logoSrc ? 'REMOVE LOGO' : 'UPLOAD LOGO';
    const hover = uploadingLogo ? null : (
      <div className={ classes.editOverlay }>
        <Button
          className={ classes.action }
          color="black"
          onClick={ this.onUploadLogo }
        >
          { hoverTitle }
        </Button>
      </div>
    );

    return (
      <>
        <div className={ classes.divider } />
        <div className={ classes.logoSection }>
          { content }
          { hover }
        </div>
      </>
    );
  }

  render() {
    const {
      name, description, totalPrice, worksExtended, updateAttributes, currency
    } = this.props;

    const { showPrice, editingDescription, editingAttributes } = this.state;
    const price = showPrice ? (
      <div>
        Total Price:&nbsp;
        { currency }
        <strong>
          { totalPrice.toFixed(2) }
        </strong>
      </div>
    ) : null;

    const renderDesc = description ? (
      <div className={ classes.description }>
        <strong>Wall Descripiton:&nbsp;</strong>
        { description }
      </div>
    ) : (
      <div className={ classes.addDescription }>
        ADD WALL DESCRIPTION
      </div>
    );

    const detailsSection = editingDescription ? (
      <EditDescription
        updateAttributes={ updateAttributes }
        description={ description }
        name={ name }
        onCancel={ this.onCancelEditingDescription }
      />
    ) : (
      <>
        <div className={ classes.title }>
          <strong>Wall Title:&nbsp;</strong>
          { name }
        </div>
        { renderDesc }
        <div className={ classes.editOverlay }>
          <Button
            className={ classes.action }
            color="black"
            onClick={ this.onEditDescription }
          >
            EDIT
          </Button>
        </div>
      </>
    );

    const renderAttributes = (editingAttributes && window.canPublish) ? (
      <>
        <h4 className={ shared.h4 }>
          Attributes
        </h4>
        <Attributes onDone={ this.stopEditingAttributes } />
      </>
    ) : null;

    return (
      <PanelLayout title="Save" onClose={ this.closePanel }>
        <div className={ shared.container }>
          <div className={ shared.holder }>
            <div className={ classnames(shared.scroll, classes.scroll) }>
              <h4 className={ shared.h4 }>
                General Info
              </h4>
              <div className={ classes.preview }>
                <Preview
                  display={ false }
                  { ...this.props }
                  currentColor="#fff"
                  onBuildComplete={ this.onBuildCompleteTransparent }
                  onBuildStarted={ this.onBuildStartedTransparent }
                />
                <Preview
                  display
                  { ...this.props }
                  onBuildComplete={ this.onBuildComplete }
                  onBuildStarted={ this.onBuildStarted }
                  onClose={ this.closePanel }
                />
                <div className={ classes.divider } />
                <div className={ classes.detailsSection }>
                  { detailsSection }
                </div>
                { window.allowLogo ? this.renderLogo() : null }
              </div>
              { renderAttributes }
              <h4 className={ classnames(shared.h4, classes.details) }>
                <span className={ classes.title }>
                  Art Details
                </span>
                <div className={ classes.totals }>
                  <strong>
                    { worksExtended.length }
                    &nbsp;items(s)
                  </strong>
                  { price }
                </div>
              </h4>
              <div>
                { this.renderWorks() }
              </div>
            </div>
          </div>
          <div className={ classes.actions }>
            { this.renderActions() }
          </div>
        </div>
      </PanelLayout>
    );
  }
}
